import { useTranslation } from "react-i18next";
import type {
  PersonRequestHistoryItem,
  ProviderChange,
} from "../../api/requestsAdmin";
import { isProviderAllocationRequest } from "../../api/requestTypes";
import { ProviderName } from "../shared/ProviderName";
import { LoadingState } from "../states";
import card from "../tools/ToolCard.module.css";
import { RequestDetailContent } from "./RequestHistoryTable";
import { RequestWhen } from "./RequestTimeline";
import { CancelRequestButton } from "./CancelRequestButton";
import { WaitingWith } from "./WaitingWith";
import {
  AwaitingDependenciesStatus,
  formatRelative,
  shortEmail,
  isChanged,
  statusLabel,
  typeLabel,
} from "./requestHistoryFormat";
import styles from "./RequestTiles.module.css";

const wholeDollars = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/** The chip tone for a request's status: done is green, refused red, withdrawn grey, and anything still waiting amber. */
function statusTone(status: string): string {
  switch (status) {
    case "Approved":
    case "Completed":
      return card.chipSuccess;
    case "Denied":
      return card.chipDanger;
    case "Withdrawn":
      return card.chipNeutral;
    default:
      return card.chipWarning;
  }
}

/**
 * A provider-allocation request's changes as one line per tool, "before → after", for a screen too narrow for the
 * before-and-after grid. Only the tools that change are listed; the rest would only repeat the current split.
 *
 * @param props.changes - The providers the request lists, each with its before and after setting.
 */
function ProviderChangeList({
  changes,
}: {
  changes: ProviderChange[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const changed = changes.filter(isChanged);
  const setting = (enabled: boolean | null, amount: number | null): string =>
    enabled === true
      ? wholeDollars.format(amount ?? 0)
      : t("requests.tiles.off", "Off");

  if (changed.length === 0) {
    return (
      <p className={styles.muted}>
        {t(
          "requests.history.details.noChange",
          "No tool changes are recorded for this request.",
        )}
      </p>
    );
  }

  return (
    <ul
      className={styles.changes}
      aria-label={t("requests.history.details.change", "Requested change")}
    >
      {changed.map((change) => (
        <li key={change.vendorId} className={styles.change}>
          <span className={styles.tool}>
            <ProviderName
              vendorId={change.vendorId}
              name={t(
                `admin.entitlement.person.allocation.vendors.${change.vendorId}`,
                change.vendorId,
              )}
            />
          </span>
          <span className={styles.values}>
            <span className={styles.before}>
              {setting(change.beforeEnabled, change.beforeAmountUsd)}
            </span>
            <span aria-hidden="true"> → </span>
            <span className="visually-hidden">
              {t("requests.tiles.to", " to ")}
            </span>
            <strong>
              {setting(change.afterEnabled, change.afterAmountUsd)}
            </strong>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A single tile holding the spinner while the requests load, so the tiles arrive in its place.
 *
 * @param props.label - What is loading, shown beside the spinner.
 */
export function RequestTileLoading({
  label,
}: {
  label: string;
}): React.JSX.Element {
  return (
    <div className={styles.tile}>
      <LoadingState label={label} />
    </div>
  );
}

/**
 * Requests as a list of tiles, for a phone, where the history table would need sideways scrolling: each tile names
 * the request, its state and when it was logged or decided, and says what it asks for. A decided request also says who
 * decided it and shows their notes.
 *
 * @param props.items - The requests to show, most recent first.
 * @param props.label - The list's accessible name.
 * @param props.onCancelled - When given, a pending request can be cancelled, and this is called once it has been.
 */
export function RequestTiles({
  items,
  label,
  onCancelled,
}: {
  items: PersonRequestHistoryItem[];
  label: string;
  onCancelled?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const whenText = (item: PersonRequestHistoryItem): string => {
    if (item.decidedAt === null) {
      return t("requests.tiles.logged", "Logged {{date}}", {
        date: formatRelative(item.createdAt),
      });
    }

    const name = item.decidedByName ?? shortEmail(item.decidedByEmail);
    const date = formatRelative(item.decidedAt);
    if (item.status === AwaitingDependenciesStatus) {
      return name
        ? t("requests.tiles.approvedBy", "Approved {{date}} by {{name}}", {
            date,
            name,
          })
        : t("requests.tiles.approved", "Approved {{date}}", { date });
    }

    if (item.status === "Withdrawn") {
      return t("requests.tiles.withdrawn", "Withdrawn {{date}}", { date });
    }

    if (item.status === "Denied") {
      return name
        ? t("requests.tiles.deniedBy", "Denied {{date}} by {{name}}", {
            date,
            name,
          })
        : t("requests.tiles.denied", "Denied {{date}}", { date });
    }

    return name
      ? t("requests.tiles.completedBy", "Completed {{date}} by {{name}}", {
          date,
          name,
        })
      : t("requests.tiles.completed", "Completed {{date}}", { date });
  };

  return (
    <ul className={styles.tiles} aria-label={label}>
      {items.map((item) => (
        <li key={item.id} className={styles.tile}>
          <div className={styles.header}>
            <h3 className={styles.title}>
              {typeLabel(item.requestType, true)}
            </h3>
            <span className={`${card.chip} ${statusTone(item.status)}`}>
              {statusLabel(item.status)}
            </span>
          </div>
          <RequestWhen item={item} className={styles.logged}>
            {whenText(item)}
          </RequestWhen>
          <WaitingWith item={item} />
          {item.decisionNotes && (
            <p className={styles.notes}>{item.decisionNotes}</p>
          )}
          {isProviderAllocationRequest(item.requestType) ? (
            <ProviderChangeList changes={item.providerChanges ?? []} />
          ) : (
            <div className={styles.details}>
              <RequestDetailContent
                requestType={item.requestType}
                payloadJson={item.payloadJson}
                providerChanges={item.providerChanges}
                forRequester
                status={item.status}
              />
            </div>
          )}
          {onCancelled && (
            <CancelRequestButton
              requestId={item.id}
              status={item.status}
              onCancelled={onCancelled}
            />
          )}
        </li>
      ))}
    </ul>
  );
}
